import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Switch } from './ui/switch';
import NJMMISMFAContactCard from './NJMMISMFAContactCard';
import { 
  Settings, 
  Shield, 
  CheckCircle, 
  XCircle, 
  Loader2, 
  Eye, 
  EyeOff,
  RefreshCw,
  Link2,
  AlertTriangle,
  Building2,
  FileText,
  Wifi,
  Calendar,
  Bell,
  Key,
  ExternalLink,
  Mic,
  BarChart3,
  FlaskConical
} from 'lucide-react';

const IntegrationSettings = ({ currentUser, organization, token, toast, onUpdate }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [settings, setSettings] = useState(null);
  const [testResults, setTestResults] = useState(null);
  
  // Form states for PerformCare
  const [pcEnabled, setPcEnabled] = useState(false);
  const [pcUsername, setPcUsername] = useState('');
  const [pcPassword, setPcPassword] = useState('');
  const [pcAgencyId, setPcAgencyId] = useState('');
  const [pcSyncEnabled, setPcSyncEnabled] = useState(false);
  const [showPcPassword, setShowPcPassword] = useState(false);
  
  // Form states for NJMMIS
  const [njEnabled, setNjEnabled] = useState(false);
  const [njUserId, setNjUserId] = useState('');
  const [njPassword, setNjPassword] = useState('');
  const [njSubmitterId, setNjSubmitterId] = useState('');
  const [njProviderNpi, setNjProviderNpi] = useState('');
  const [njProviderTaxId, setNjProviderTaxId] = useState('');
  const [njSyncEnabled, setNjSyncEnabled] = useState(false);
  const [showNjPassword, setShowNjPassword] = useState(false);
  
  // Clearinghouse
  const [clearinghouseEnabled, setClearinghouseEnabled] = useState(false);
  const [clearinghouseName, setClearinghouseName] = useState('');
  
  // Google Calendar Integration
  const [gcalEnabled, setGcalEnabled] = useState(false);
  const [gcalClientId, setGcalClientId] = useState('');
  const [gcalClientSecret, setGcalClientSecret] = useState('');
  const [gcalSyncEnabled, setGcalSyncEnabled] = useState(false);
  const [gcalConnected, setGcalConnected] = useState(false);
  const [showGcalSecret, setShowGcalSecret] = useState(false);
  const [gcalConnecting, setGcalConnecting] = useState(false);
  const [gcalDisconnecting, setGcalDisconnecting] = useState(false);
  
  // Push Notifications (Firebase)
  const [pushEnabled, setPushEnabled] = useState(false);
  const [firebaseProjectId, setFirebaseProjectId] = useState('');
  const [firebaseApiKey, setFirebaseApiKey] = useState('');
  const [firebaseVapidKey, setFirebaseVapidKey] = useState('');
  const [showFirebaseKey, setShowFirebaseKey] = useState(false);
  
  // Voice-to-Text (Whisper)
  const [voiceToTextEnabled, setVoiceToTextEnabled] = useState(false);
  const [voiceToTextStatus, setVoiceToTextStatus] = useState(null);
  const [voiceToTextUsage, setVoiceToTextUsage] = useState(null);
  const [voiceToTextLoading, setVoiceToTextLoading] = useState(false);

  // Portal Mock Mode (Super Admin only)
  const [mockModeEnabled, setMockModeEnabled] = useState(false);
  const [mockModeLoading, setMockModeLoading] = useState(false);
  const [mockModeSource, setMockModeSource] = useState('');

  const orgId = organization?.org_id || organization?.id;
  const isSuperAdmin = currentUser?.role === 'super_admin' || sessionStorage.getItem('is_impersonating') === 'true' || localStorage.getItem('is_impersonating') === 'true';

  useEffect(() => {
    if (orgId) {
      fetchSettings();
      fetchVoiceToTextStatus();
    }
    if (isSuperAdmin) {
      fetchMockMode();
    }
  }, [orgId]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/organizations/${orgId}/integrations`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const data = response.data.integration_settings;
      setSettings(data);
      
      // Populate PerformCare fields
      if (data.performcare) {
        setPcEnabled(data.performcare.enabled || false);
        setPcUsername(data.performcare.username || '');
        setPcAgencyId(data.performcare.agency_id || '');
        setPcSyncEnabled(data.performcare.sync_enabled || false);
      }
      
      // Populate NJMMIS fields
      if (data.njmmis) {
        setNjEnabled(data.njmmis.enabled || false);
        setNjUserId(data.njmmis.user_id || '');
        setNjSubmitterId(data.njmmis.submitter_id || '');
        setNjProviderNpi(data.njmmis.provider_npi || '');
        setNjProviderTaxId(data.njmmis.provider_tax_id || '');
        setNjSyncEnabled(data.njmmis.sync_enabled || false);
      }
      
      // Clearinghouse
      setClearinghouseEnabled(data.clearinghouse_enabled || false);
      setClearinghouseName(data.clearinghouse_name || '');
      
      // Google Calendar
      if (data.google_calendar) {
        setGcalEnabled(data.google_calendar.enabled || false);
        setGcalClientId(data.google_calendar.client_id || '');
        setGcalSyncEnabled(data.google_calendar.sync_enabled || false);
        setGcalConnected(data.google_calendar.connected || false);
      }
      
      // Push Notifications
      if (data.push_notifications) {
        setPushEnabled(data.push_notifications.enabled || false);
        setFirebaseProjectId(data.push_notifications.project_id || '');
        setFirebaseVapidKey(data.push_notifications.vapid_key || '');
      }
      
    } catch (error) {
      console.error('Error fetching integration settings:', error);
      toast?.error('Failed to load integration settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchVoiceToTextStatus = async () => {
    try {
      setVoiceToTextLoading(true);
      
      // Get status
      const statusResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/voice-to-text/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVoiceToTextStatus(statusResponse.data);
      setVoiceToTextEnabled(statusResponse.data.enabled || false);
      
      // Get usage stats if enabled
      if (statusResponse.data.enabled) {
        try {
          const usageResponse = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/voice-to-text/usage`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (usageResponse.data.success) {
            setVoiceToTextUsage(usageResponse.data.stats);
          }
        } catch (usageError) {
          console.log('Usage stats not available:', usageError);
        }
      }
    } catch (error) {
      console.error('Error fetching voice-to-text status:', error);
    } finally {
      setVoiceToTextLoading(false);
    }
  };

  const toggleVoiceToText = async (enabled) => {
    try {
      setVoiceToTextLoading(true);
      
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/voice-to-text/toggle`,
        { enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setVoiceToTextEnabled(enabled);
      toast?.success(`Voice-to-Text ${enabled ? 'enabled' : 'disabled'} successfully`);
      
      // Refresh status
      await fetchVoiceToTextStatus();
      
    } catch (error) {
      console.error('Error toggling voice-to-text:', error);
      toast?.error('Failed to update Voice-to-Text setting');
    } finally {
      setVoiceToTextLoading(false);
    }
  };

  const fetchMockMode = async () => {
    try {
      setMockModeLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/njmmis/live/mock-mode`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMockModeEnabled(res.data.mock_mode || false);
      setMockModeSource(res.data.source || '');
    } catch (error) {
      console.log('Mock mode status not available:', error);
    } finally {
      setMockModeLoading(false);
    }
  };

  const toggleMockMode = async (enabled) => {
    try {
      setMockModeLoading(true);
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/njmmis/live/mock-mode`,
        { enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMockModeEnabled(enabled);
      toast?.success(`Portal Mock Mode ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error toggling mock mode:', error);
      toast?.error('Failed to update mock mode');
    } finally {
      setMockModeLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      
      const payload = {
        performcare: {
          enabled: pcEnabled,
          username: pcUsername,
          agency_id: pcAgencyId,
          sync_enabled: pcSyncEnabled,
          ...(pcPassword && { password: pcPassword })
        },
        njmmis: {
          enabled: njEnabled,
          user_id: njUserId,
          submitter_id: njSubmitterId,
          provider_npi: njProviderNpi,
          provider_tax_id: njProviderTaxId,
          sync_enabled: njSyncEnabled,
          ...(njPassword && { password: njPassword })
        },
        clearinghouse_enabled: clearinghouseEnabled,
        clearinghouse_name: clearinghouseName,
        google_calendar: {
          enabled: gcalEnabled,
          client_id: gcalClientId,
          sync_enabled: gcalSyncEnabled,
          ...(gcalClientSecret && { client_secret: gcalClientSecret })
        },
        push_notifications: {
          enabled: pushEnabled,
          project_id: firebaseProjectId,
          vapid_key: firebaseVapidKey,
          ...(firebaseApiKey && { api_key: firebaseApiKey })
        }
      };
      
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/organizations/${orgId}/integrations`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast?.success('✅ Integration settings saved successfully');
      
      // Clear password fields after save
      setPcPassword('');
      setNjPassword('');
      
      // Refresh settings
      await fetchSettings();
      
      if (onUpdate) onUpdate();
      
    } catch (error) {
      console.error('Error saving integration settings:', error);
      toast?.error(`Failed to save settings: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const testConnections = async () => {
    try {
      setTesting(true);
      setTestResults(null);
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/organizations/${orgId}/integrations/test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTestResults(response.data.results);
      
      if (response.data.all_connected) {
        toast?.success('✅ All integrations connected successfully!');
      } else {
        toast?.info('ℹ️ Connection test completed. Check results below.');
      }
      
    } catch (error) {
      console.error('Error testing connections:', error);
      toast?.error(`Connection test failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setTesting(false);
    }
  };

  // Google Calendar OAuth Connect
  const connectGoogleCalendar = async () => {
    if (!gcalClientId || !gcalClientSecret) {
      toast?.error('Please save your Client ID and Client Secret first');
      return;
    }

    try {
      setGcalConnecting(true);
      
      // Save credentials first
      await saveSettings();
      
      // Get the OAuth URL from backend
      const redirectUri = `${window.location.origin}/auth/google/callback`;
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/integrations/google-calendar/auth-url`,
        { 
          params: { redirect_uri: redirectUri },
          headers: { Authorization: `Bearer ${token}` } 
        }
      );
      
      if (response.data.auth_url) {
        // Store current page to return to after OAuth
        localStorage.setItem('gcal_oauth_return', window.location.pathname);
        // Redirect to Google OAuth
        window.location.href = response.data.auth_url;
      } else {
        toast?.error('Failed to get authorization URL');
      }
    } catch (error) {
      console.error('Error connecting Google Calendar:', error);
      toast?.error(`Connection failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setGcalConnecting(false);
    }
  };

  // Google Calendar Disconnect
  const disconnectGoogleCalendar = async () => {
    if (!window.confirm('Are you sure you want to disconnect Google Calendar? Existing calendar sync will stop.')) {
      return;
    }

    try {
      setGcalDisconnecting(true);
      
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/integrations/google-calendar/disconnect`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setGcalConnected(false);
      toast?.success('✅ Google Calendar disconnected');
      await fetchSettings();
      
    } catch (error) {
      console.error('Error disconnecting Google Calendar:', error);
      toast?.error(`Disconnect failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setGcalDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        <span className="ml-3 text-gray-600">Loading integration settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Link2 className="w-5 h-5 text-teal-600" />
            External System Integrations
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Connect to PerformCare CYBER and NJMMIS for seamless data synchronization
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={testConnections}
            disabled={testing}
            className="gap-2"
          >
            {testing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Wifi className="w-4 h-4" />
            )}
            Test Connections
          </Button>
          <Button 
            onClick={saveSettings}
            disabled={saving}
            className="bg-teal-600 hover:bg-teal-700 gap-2"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4" />
            )}
            Save Settings
          </Button>
        </div>
      </div>

      {/* Connection Test Results */}
      {testResults && (
        <Card className={`border-2 ${testResults.performcare?.success && testResults.njmmis?.success ? 'border-green-200 bg-green-50' : 'border-yellow-200 bg-yellow-50'}`}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              Connection Test Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                {testResults.performcare?.success ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
                <div>
                  <p className="font-medium">PerformCare</p>
                  <p className="text-xs text-gray-500">
                    {testResults.performcare?.message || testResults.performcare?.status || 'Not configured'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                {testResults.njmmis?.success ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
                <div>
                  <p className="font-medium">NJMMIS</p>
                  <p className="text-xs text-gray-500">
                    {testResults.njmmis?.message || testResults.njmmis?.status || 'Not configured'}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="njmmis" className="space-y-4">
        <TabsList className={`bg-gray-100 grid ${isSuperAdmin ? 'grid-cols-6' : 'grid-cols-5'}`}>
          <TabsTrigger value="njmmis" className="gap-2">
            <FileText className="w-4 h-4" />
            NJMMIS
          </TabsTrigger>
          <TabsTrigger value="clearinghouse" className="gap-2">
            <Settings className="w-4 h-4" />
            Clearinghouse
          </TabsTrigger>
          <TabsTrigger value="google-calendar" className="gap-2">
            <Calendar className="w-4 h-4" />
            Google Cal
          </TabsTrigger>
          <TabsTrigger value="push-notifications" className="gap-2">
            <Bell className="w-4 h-4" />
            Push Alerts
          </TabsTrigger>
          <TabsTrigger value="voice-to-text" className="gap-2">
            <Mic className="w-4 h-4" />
            Voice-to-Text
          </TabsTrigger>
          {isSuperAdmin && (
            <TabsTrigger value="portal-testing" className="gap-2" data-testid="portal-testing-tab">
              <FlaskConical className="w-4 h-4" />
              Portal Testing
            </TabsTrigger>
          )}
        </TabsList>

        {/* NJMMIS Tab */}
        <TabsContent value="njmmis">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-purple-600" />
                    NJMMIS Medicaid Integration
                  </CardTitle>
                  <CardDescription>
                    Submit claims (837P) and receive remittance (835) from NJ Medicaid
                  </CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600">Enable Integration</span>
                  <Switch 
                    checked={njEnabled} 
                    onCheckedChange={setNjEnabled}
                  />
                  {njEnabled ? (
                    <Badge className="bg-green-100 text-green-800">Active</Badge>
                  ) : (
                    <Badge variant="outline" className="text-gray-500">Disabled</Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {njEnabled && (
                <>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-purple-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-purple-900">Provider Identification Required</p>
                        <p className="text-xs text-purple-700 mt-1">
                          NPI and Tax ID are required for 837P claim submission. Ensure these match your 
                          NJMMIS provider enrollment records exactly.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="nj-userid">User ID *</Label>
                      <Input 
                        id="nj-userid"
                        placeholder="NJMMIS User ID"
                        value={njUserId}
                        onChange={(e) => setNjUserId(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nj-password">
                        Password {settings?.njmmis?.password_configured ? '(configured)' : '*'}
                      </Label>
                      <div className="relative">
                        <Input 
                          id="nj-password"
                          type={showNjPassword ? 'text' : 'password'}
                          placeholder={settings?.njmmis?.password_configured ? '••••••••' : 'Enter password'}
                          value={njPassword}
                          onChange={(e) => setNjPassword(e.target.value)}
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          onClick={() => setShowNjPassword(!showNjPassword)}
                        >
                          {showNjPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {settings?.njmmis?.password_configured && (
                        <p className="text-xs text-gray-500">Leave blank to keep existing password</p>
                      )}
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-medium text-gray-900 mb-3">Provider Information</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="nj-submitter">Submitter ID</Label>
                        <Input 
                          id="nj-submitter"
                          placeholder="EDI Submitter ID"
                          value={njSubmitterId}
                          onChange={(e) => setNjSubmitterId(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="nj-npi">Provider NPI *</Label>
                        <Input 
                          id="nj-npi"
                          placeholder="10-digit NPI"
                          value={njProviderNpi}
                          onChange={(e) => setNjProviderNpi(e.target.value)}
                          maxLength={10}
                        />
                        <p className="text-xs text-gray-500">e.g., 1356986319</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="nj-tax">Provider Tax ID</Label>
                        <Input 
                          id="nj-tax"
                          placeholder="EIN or SSN"
                          value={njProviderTaxId}
                          onChange={(e) => setNjProviderTaxId(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">Auto-Sync Claims</h4>
                        <p className="text-sm text-gray-500">Automatically submit claims and retrieve remittance</p>
                      </div>
                      <Switch 
                        checked={njSyncEnabled} 
                        onCheckedChange={setNjSyncEnabled}
                      />
                    </div>
                  </div>

                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-medium text-gray-900 mb-3">EDI Capabilities</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        837P Professional Claims
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        835 Electronic Remittance
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        270/271 Eligibility
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        276/277 Claim Status
                      </div>
                    </div>
                  </div>
                </>
              )}
              
              {!njEnabled && (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Enable NJMMIS integration to configure Medicaid billing settings</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* NJMMIS MFA Intercept readiness — Nov 13 deadline. See
              /app/memory/NJMMIS_MFA_INTERCEPT_PLAN.md. Feature-flag
              gated server-side (organizations.feature_flags.mfa_intercept_enabled). */}
          <NJMMISMFAContactCard token={token} toast={toast} />
        </TabsContent>
        <TabsContent value="clearinghouse">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5 text-orange-600" />
                    Clearinghouse (Alternative to Direct NJMMIS)
                  </CardTitle>
                  <CardDescription>
                    Use a third-party clearinghouse for claims submission instead of direct NJMMIS connection
                  </CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600">Use Clearinghouse</span>
                  <Switch 
                    checked={clearinghouseEnabled} 
                    onCheckedChange={setClearinghouseEnabled}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {clearinghouseEnabled && (
                <div className="space-y-4">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-orange-900">Clearinghouse Configuration</p>
                        <p className="text-xs text-orange-700 mt-1">
                          When using a clearinghouse, claims are routed through your clearinghouse 
                          provider instead of direct NJMMIS submission. Configure your clearinghouse 
                          credentials in the EDI Settings tab.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="clearinghouse-name">Clearinghouse Provider</Label>
                    <select
                      id="clearinghouse-name"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                      value={clearinghouseName}
                      onChange={(e) => setClearinghouseName(e.target.value)}
                    >
                      <option value="">Select a clearinghouse...</option>
                      <option value="office_ally">Office Ally</option>
                      <option value="availity">Availity</option>
                      <option value="trizetto">TriZetto</option>
                      <option value="change_healthcare">Change Healthcare</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <p className="text-sm text-gray-500 mt-4">
                    Detailed clearinghouse settings can be configured in 
                    <strong> Admin → Settings → Clearinghouse (EDI)</strong>
                  </p>
                </div>
              )}
              
              {!clearinghouseEnabled && (
                <div className="text-center py-8 text-gray-500">
                  <Settings className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Enable to use a clearinghouse instead of direct NJMMIS connection</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Google Calendar Tab */}
        <TabsContent value="google-calendar">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    Google Calendar Integration
                  </CardTitle>
                  <CardDescription>
                    Sync appointments with Google Calendar for therapists and staff
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Enable</span>
                  <Switch
                    checked={gcalEnabled}
                    onCheckedChange={setGcalEnabled}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {gcalEnabled && (
                <div className="space-y-4">
                  {/* Setup Instructions */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                      <Key className="w-4 h-4" />
                      Setup Instructions
                    </h4>
                    <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                      <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></li>
                      <li>Create a project or select existing one</li>
                      <li>Enable the Google Calendar API</li>
                      <li>Create OAuth 2.0 credentials (Web application)</li>
                      <li>Add your redirect URI: <code className="bg-blue-100 px-1 rounded">{window.location.origin}/auth/google/callback</code></li>
                      <li>Copy Client ID and Client Secret below</li>
                    </ol>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Client ID</Label>
                      <Input
                        value={gcalClientId}
                        onChange={(e) => setGcalClientId(e.target.value)}
                        placeholder="xxx.apps.googleusercontent.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Client Secret</Label>
                      <div className="relative">
                        <Input
                          type={showGcalSecret ? 'text' : 'password'}
                          value={gcalClientSecret}
                          onChange={(e) => setGcalClientSecret(e.target.value)}
                          placeholder="Enter client secret"
                        />
                        <button
                          type="button"
                          onClick={() => setShowGcalSecret(!showGcalSecret)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showGcalSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Switch
                      checked={gcalSyncEnabled}
                      onCheckedChange={setGcalSyncEnabled}
                    />
                    <div>
                      <p className="font-medium text-sm">Two-Way Sync</p>
                      <p className="text-xs text-gray-500">Automatically sync CounselCore appointments to Google Calendar and vice versa</p>
                    </div>
                  </div>

                  {/* Connection Status & Actions */}
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-medium text-gray-900 mb-3">Connection Status</h4>
                    {gcalConnected ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <span className="text-green-800 font-medium">Google Calendar Connected</span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={disconnectGoogleCalendar}
                            disabled={gcalDisconnecting}
                          >
                            {gcalDisconnecting ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                              <XCircle className="w-4 h-4 mr-2" />
                            )}
                            Disconnect
                          </Button>
                        </div>
                        <p className="text-sm text-gray-500">
                          Calendar events are synced automatically. Use Manual Sync to force refresh.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-yellow-600" />
                            <span className="text-yellow-800">Not Connected - OAuth authorization required</span>
                          </div>
                          <Button
                            className="bg-blue-600 hover:bg-blue-700"
                            size="sm"
                            onClick={connectGoogleCalendar}
                            disabled={gcalConnecting || !gcalClientId}
                          >
                            {gcalConnecting ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                              <ExternalLink className="w-4 h-4 mr-2" />
                            )}
                            Connect Google Calendar
                          </Button>
                        </div>
                        <p className="text-sm text-gray-500">
                          Click Connect to authorize CounselCore to access your organization&apos;s Google Calendar. 
                          You&apos;ll be redirected to Google to grant permission.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {!gcalEnabled && (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Enable to sync appointments with Google Calendar</p>
                  <p className="text-sm mt-1">Therapists can see their CounselCore appointments in their personal calendar</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Push Notifications Tab */}
        <TabsContent value="push-notifications">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5 text-amber-600" />
                    Browser Push Notifications
                  </CardTitle>
                  <CardDescription>
                    Send push notifications for appointments, messages, and alerts
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Enable</span>
                  <Switch
                    checked={pushEnabled}
                    onCheckedChange={setPushEnabled}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {pushEnabled && (
                <div className="space-y-4">
                  {/* Setup Instructions */}
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <h4 className="font-medium text-amber-900 mb-2 flex items-center gap-2">
                      <Key className="w-4 h-4" />
                      Firebase Setup Instructions
                    </h4>
                    <ol className="text-sm text-amber-800 space-y-1 list-decimal list-inside">
                      <li>Go to <a href="https://console.firebase.google.com" target="_blank" rel="noopener noreferrer" className="underline">Firebase Console</a></li>
                      <li>Create a new project or select existing</li>
                      <li>Go to Project Settings → Cloud Messaging</li>
                      <li>Generate a new Web Push certificate (VAPID key)</li>
                      <li>Copy the credentials below</li>
                    </ol>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Firebase Project ID</Label>
                      <Input
                        value={firebaseProjectId}
                        onChange={(e) => setFirebaseProjectId(e.target.value)}
                        placeholder="my-counselcore-app"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>API Key</Label>
                      <div className="relative">
                        <Input
                          type={showFirebaseKey ? 'text' : 'password'}
                          value={firebaseApiKey}
                          onChange={(e) => setFirebaseApiKey(e.target.value)}
                          placeholder="AIzaSy..."
                        />
                        <button
                          type="button"
                          onClick={() => setShowFirebaseKey(!showFirebaseKey)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showFirebaseKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>VAPID Key (Web Push Certificate)</Label>
                      <Input
                        value={firebaseVapidKey}
                        onChange={(e) => setFirebaseVapidKey(e.target.value)}
                        placeholder="BHJk..."
                      />
                      <p className="text-xs text-gray-500">Found in Firebase Console → Project Settings → Cloud Messaging → Web Push certificates</p>
                    </div>
                  </div>

                  {/* Notification Types */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-3">Notification Types</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>Appointment reminders (24hr, 1hr before)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>New message notifications</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>Task assignments and due dates</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>Session note approval requests</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {!pushEnabled && (
                <div className="text-center py-8 text-gray-500">
                  <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Enable browser push notifications</p>
                  <p className="text-sm mt-1">Staff receive real-time alerts for appointments, messages, and tasks</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Voice-to-Text Tab */}
        <TabsContent value="voice-to-text">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Mic className="w-5 h-5 text-teal-600" />
                    Voice-to-Text (Whisper AI)
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Transcribe therapy session recordings and generate clinical notes automatically
                  </CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  {voiceToTextLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                  ) : (
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={voiceToTextEnabled}
                        onChange={(e) => toggleVoiceToText(e.target.checked)}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
                    </label>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Status Card */}
              <div className={`p-4 rounded-lg border ${voiceToTextEnabled ? 'bg-teal-50 border-teal-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center gap-3">
                  {voiceToTextEnabled ? (
                    <CheckCircle className="w-5 h-5 text-teal-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-gray-400" />
                  )}
                  <div>
                    <p className={`font-medium ${voiceToTextEnabled ? 'text-teal-900' : 'text-gray-700'}`}>
                      {voiceToTextEnabled ? 'Voice-to-Text is Enabled' : 'Voice-to-Text is Disabled'}
                    </p>
                    <p className="text-sm text-gray-600 mt-0.5">
                      {voiceToTextEnabled 
                        ? 'Your team can transcribe session recordings and generate notes.'
                        : 'Enable to allow audio transcription and AI note generation.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Usage Stats (when enabled) */}
              {voiceToTextEnabled && voiceToTextUsage && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white border rounded-lg p-4 text-center">
                    <div className="flex items-center justify-center gap-2 text-gray-500 mb-1">
                      <FileText className="w-4 h-4" />
                      <span className="text-xs font-medium">Transcriptions</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{voiceToTextUsage.total_transcriptions || 0}</p>
                  </div>
                  <div className="bg-white border rounded-lg p-4 text-center">
                    <div className="flex items-center justify-center gap-2 text-gray-500 mb-1">
                      <Mic className="w-4 h-4" />
                      <span className="text-xs font-medium">Audio Minutes</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{voiceToTextUsage.total_audio_minutes || 0}</p>
                  </div>
                  <div className="bg-white border rounded-lg p-4 text-center">
                    <div className="flex items-center justify-center gap-2 text-gray-500 mb-1">
                      <BarChart3 className="w-4 h-4" />
                      <span className="text-xs font-medium">Est. Cost</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">${voiceToTextUsage.estimated_cost || '0.00'}</p>
                  </div>
                </div>
              )}

              {/* Features List */}
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900">Features Included:</h4>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircle className="w-4 h-4 text-teal-500" />
                    OpenAI Whisper transcription (supports MP3, WAV, M4A, WebM)
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircle className="w-4 h-4 text-teal-500" />
                    Auto-generated clinical notes (SOAP, DAP, BIRP, GDAP formats)
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircle className="w-4 h-4 text-teal-500" />
                    HIPAA-compliant processing
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircle className="w-4 h-4 text-teal-500" />
                    Up to 25MB file size per upload
                  </li>
                </ul>
              </div>

              {/* Quick Access Button */}
              {voiceToTextEnabled && (
                <div className="pt-4 border-t">
                  <Button
                    onClick={() => window.location.href = '/voice-to-text'}
                    className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700"
                  >
                    <Mic className="w-4 h-4 mr-2" />
                    Open Voice-to-Text Tool
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}

              {/* Pricing Info */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-900">Usage-Based Pricing</h4>
                    <p className="text-sm text-amber-800 mt-1">
                      Voice-to-Text usage is tracked and may be billed based on audio minutes transcribed.
                      Contact your account manager for pricing details.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Portal Testing Tab (Super Admin Only) */}
        {isSuperAdmin && (
          <TabsContent value="portal-testing">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <FlaskConical className="w-5 h-5 text-violet-600" />
                      Portal Mock Mode
                    </CardTitle>
                    <CardDescription>
                      Route all NJMMIS and PerformCare portal requests through simulated mock services
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-3">
                    {mockModeLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                    ) : (
                      <Switch
                        data-testid="mock-mode-toggle"
                        checked={mockModeEnabled}
                        onCheckedChange={toggleMockMode}
                      />
                    )}
                    {mockModeEnabled ? (
                      <Badge className="bg-amber-100 text-amber-800" data-testid="mock-mode-badge-active">Mock</Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-800" data-testid="mock-mode-badge-live">Live</Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Status Card */}
                <div className={`p-4 rounded-lg border ${mockModeEnabled ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}
                  data-testid="mock-mode-status-card"
                >
                  <div className="flex items-center gap-3">
                    {mockModeEnabled ? (
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    )}
                    <div>
                      <p className={`font-medium ${mockModeEnabled ? 'text-amber-900' : 'text-green-900'}`}>
                        {mockModeEnabled ? 'Mock Mode is ACTIVE' : 'Live Mode — Connected to Real Portals'}
                      </p>
                      <p className="text-sm text-gray-600 mt-0.5">
                        {mockModeEnabled
                          ? 'All portal interactions return simulated responses. No real data is sent to NJMMIS or PerformCare.'
                          : 'Portal operations connect to production NJMMIS and PerformCare CYBER systems.'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* What Mock Mode Affects */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Services Affected by Mock Mode</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                      <FileText className="w-4 h-4 text-purple-600" />
                      <div>
                        <p className="font-medium">NJMMIS Portal</p>
                        <p className="text-xs text-gray-500">Eligibility, DDE claims, status checks</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                      <Shield className="w-4 h-4 text-teal-600" />
                      <div>
                        <p className="font-medium">PerformCare CYBER</p>
                        <p className="text-xs text-gray-500">Treatment plans, batch uploads</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Config Source Info */}
                {mockModeSource && (
                  <div className="text-xs text-gray-400 pt-2 border-t">
                    Configuration source: <span className="font-mono">{mockModeSource}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Help Section */}
      <Card className="bg-gray-50 border-dashed">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <Shield className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">Need Help?</h4>
              <p className="text-sm text-gray-600 mt-1">
                For assistance with PerformCare or NJMMIS integration, contact support at{' '}
                <a href="mailto:support@counselcore.org" className="text-teal-600 hover:underline">
                  support@counselcore.org
                </a>
                {' '}or call <span className="font-medium">(856) 724-7900</span>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default IntegrationSettings;
